import React, { useMemo, useLayoutEffect, useState } from 'react';
import PromoIcon from './PromoIcon';
import { IconColumn, OfferDetails, OfferText, OfferTextColumn, SpecialOfferContainer } from './SpecialOffers.styles';

interface Offer {
  description: string;
  endDate: string;
}

interface ProcessedOffer extends Offer {
  expirationString: string;
  isFirst: boolean;
  key: string;
}

// Fast date formatter - avoids toLocaleDateString() overhead
const formatExpirationDate = (endDate: string): string => {
  if (!endDate) return '';

  const date = new Date(endDate);
  if (isNaN(date.getTime())) return '';

  // Simple MM/DD/YYYY format - much faster than toLocaleDateString
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();
  return `Expires ${month}/${day}/${year}.`;
};

interface SpecialOffersProps {
  offers?: Offer[];
}

const SpecialOffers = React.memo(({ offers = [] }: SpecialOffersProps) => {
  const [processedOffers, setProcessedOffers] = useState<ProcessedOffer[]>([]);

  // Initial render with basic data for immediate paint
  const basicOffers = useMemo(() => {
    return offers.map((offer, i) => ({
      ...offer,
      description: offer.description,
      expirationString: '', // Will be filled by useLayoutEffect
      isFirst: i === 0,
      key: `SpecialOffer-${i}`,
    }));
  }, [offers]);

  // Process dates after initial render to avoid blocking paint
  useLayoutEffect(() => {
    if (offers.length > 0) {
      requestAnimationFrame(() => {
        const processed = offers.map((offer, i) => ({
          ...offer,
          description: offer.description,
          expirationString: formatExpirationDate(offer.endDate),
          isFirst: i === 0,
          key: `SpecialOffer-${i}`,
        }));
        setProcessedOffers(processed);
      });
    }
  }, [offers]);

  const memoizedOffers = processedOffers.length > 0 ? processedOffers : basicOffers;

  const renderOffer = (processedOffer: ProcessedOffer) => {
    const { description, expirationString, isFirst, key } = processedOffer;

    return (
      <OfferDetails key={key}>
        {isFirst && <OfferText>Special offer! </OfferText>}
        <OfferText>{description}</OfferText>
        {expirationString && (
          <>
            {' '}
            <OfferText>{expirationString}</OfferText>
          </>
        )}
      </OfferDetails>
    );
  };

  if (offers.length === 0) {
    return null;
  }

  return (
    <SpecialOfferContainer>
      <IconColumn>
        <PromoIcon width="16px" height="16px" alt="Special offer icon" />
      </IconColumn>
      <OfferTextColumn>{memoizedOffers.map(renderOffer)}</OfferTextColumn>
    </SpecialOfferContainer>
  );
});

SpecialOffers.displayName = 'SpecialOffers';

export default SpecialOffers;
